import { useState, type ReactNode } from 'react';
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';
import {
  Badge,
  Button,
  Checkbox,
  DateFilter as SharedDateFilter,
  MultiSelect,
  Popover,
  Select,
  TextField,
  FilterButton,
} from '@skupreme/shared/ui';
import {
  ADVANCED_KEYS,
  VALUE_KEYS,
  DATE_OPERATORS,
  HOLD_EXCEPTION,
  clearFilters,
  countActiveFilters,
  describeDate,
  getDateRange,
  joinList,
  parseList,
  setDateRange,
  toDateOperator,
  type DateOperator,
  type FilterOption,
  type OrderFilterValues,
} from '../../lib/filters';
import { useOrdersV2 } from '../../hooks/orders-v2';
import { useActiveScope } from '@features/account-view/active';
import { DIVISION_FILTER_PARAM } from '@features/account-view/scope';
import { DivisionFilter } from './DivisionFilter';

export type OrdersFiltersProps = {
  values: OrderFilterValues;
  onChange: (patch: Record<string, string>) => void;
  marketplaces: FilterOption[];
  regions: FilterOption[];
  /** Given only on the queues where an order has a provider. */
  providers?: FilterOption[];
  users: FilterOption[];
  /** Given only on the queues where stock is what an order is waiting on. */
  outOfStock?: { isSelected: boolean; onChange: (isSelected: boolean) => void };
  /** Shipping and delivery dates are ship-by / ETA, which mean nothing once shipped. */
  hasDateFilters?: boolean;
  isLoading?: boolean;
  className?: string;
};

const TRIGGER_WIDTH = 'sk:w-44';
const DATE_FILTER_OPERATORS = DATE_OPERATORS.map(option => ({
  ...option,
  isRange: option.value === 'BETWEEN',
}));

const DATE_ROWS = [
  { name: 'handling_time', opName: 'handling_time_op', label: 'Shipping date' },
  { name: 'shipping_time', opName: 'shipping_time_op', label: 'Delivery date' },
] as const;

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="sk:flex sk:items-center sk:justify-between sk:gap-3 sk:px-3 sk:py-2">
      <span className="sk:text-dense sk:font-medium sk:text-fg">{label}</span>
      {children}
    </div>
  );
}

function Divider() {
  return <div className="sk:my-1 sk:border-t sk:border-border-subtle" />;
}

function OrdersDateFilter({
  name,
  label,
  value,
  operator,
  onChange,
}: {
  /** The query parameter this filter writes, e.g. `handling_time`. */
  name: string;
  label: string;
  value: string | undefined;
  operator: DateOperator;
  onChange: (patch: Record<string, string>) => void;
}) {
  /* `BETWEEN` needs both ends before it can be written to the query. */
  const [draft, setDraft] = useState<[string, string] | null>(null);
  const [committedFrom, committedTo] = getDateRange(value);
  const [from, to] = draft ?? [committedFrom, committedTo];

  /* An operator without a date filters nothing, so it is not written out yet. */
  const [draftOp, setDraftOp] = useState<DateOperator | null>(null);
  const op = draftOp ?? operator;

  /*
   * The half-finished range is local, so a filter cleared from outside — "Clear
   * all" in the header — would otherwise leave the old dates showing in a
   * popover whose trigger already reads "Any date".
   */
  const [lastValue, setLastValue] = useState(value);

  if (lastValue !== value) {
    setLastValue(value);

    if (!value) {
      setDraft(null);
      setDraftOp(null);
    }
  }

  /* The API needs `<name>_op` alongside the day, including the default one. */
  const setRange = (nextFrom: string, nextTo: string) => {
    setDraft([nextFrom, nextTo]);
    onChange({
      [name]: setDateRange(nextFrom, nextTo),
      [`${name}_op`]: op,
    });
  };

  return (
    <SharedDateFilter
      aria-label={label}
      triggerText={describeDate(value, op)}
      hasValue={!!value}
      operator={op}
      operators={DATE_FILTER_OPERATORS}
      from={from}
      to={to}
      onOperatorChange={next => {
        const nextOperator = toDateOperator(next);
        setDraftOp(nextOperator);

        if (!from && !to) {
          setDraft(null);
          return;
        }

        setDraft(nextOperator === 'BETWEEN' ? [from, ''] : null);
        onChange({
          [`${name}_op`]: nextOperator,
          [name]: nextOperator === 'BETWEEN' ? '' : from,
        });
      }}
      onFromChange={next => {
        if (op === 'BETWEEN') {
          setRange(next, to);
          return;
        }

        setDraft(null);
        onChange({ [name]: next, [`${name}_op`]: op });
      }}
      onToChange={next => setRange(from, next)}
      onClear={() => {
        setDraft(null);
        setDraftOp(null);
        onChange({ [name]: '' });
      }}
      className={TRIGGER_WIDTH}
    />
  );
}

/* Parameters the V2 list never honours (legacy region ids, created by, out of stock, provider): not offered at all on V2. */
const V2_IGNORED: readonly string[] = [
  'oos',
  'marketplace_region_id',
  'fulfillment_provider_id',
  'created_by',
];

export function OrdersFilters({
  values,
  onChange,
  marketplaces,
  regions,
  providers,
  users,
  outOfStock,
  hasDateFilters = true,
  isLoading,
  className,
}: OrdersFiltersProps) {
  /* EN-4117: on V2 a control whose parameter the API ignores is not offered. */
  const { isV2, notSupported } = useOrdersV2();
  const shows = (param: string) =>
    !(isV2 && (notSupported.includes(param) || V2_IGNORED.includes(param)));
  const shownOutOfStock = outOfStock && shows('oos') ? outOfStock : undefined;
  /* EN-4396: the division filter exists at the account level only, and the
     active scope (not the address bar) says which divisions are chosen. */
  const { level, divisions, divisionFilter, setDivisionFilter } =
    useActiveScope();
  const isAccount = level === 'account';
  const shownKeys = VALUE_KEYS.filter(shows).filter(
    key => key !== DIVISION_FILTER_PARAM && (isV2 || key !== 'hold'),
  );
  const advancedKeys = ADVANCED_KEYS.filter(shows);
  const active =
    countActiveFilters(values, shownKeys) +
    (shownOutOfStock?.isSelected ? 1 : 0) +
    (isAccount && divisionFilter.length > 0 ? 1 : 0);
  const activeAdvanced = countActiveFilters(values, advancedKeys);
  const [isAdvancedOpen, setAdvancedOpen] = useState(false);

  return (
    <Popover
      aria-label="Filters"
      placement="bottom end"
      className={className}
      trigger={<FilterButton count={active} isDisabled={isLoading} />}
    >
      <div className="sk:flex sk:w-80 sk:flex-col">
        <div className="sk:flex sk:items-center sk:justify-between sk:gap-6 sk:border-b sk:border-border-subtle sk:px-3 sk:py-2">
          <span className="sk:text-xs sk:font-semibold sk:text-fg-tertiary">
            Filters
          </span>
          {active > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="sk:text-dense"
              onPress={() => {
                onChange(clearFilters());
                setDivisionFilter([]);
                shownOutOfStock?.onChange(false);
              }}
            >
              Clear all
            </Button>
          ) : null}
        </div>

        {shownOutOfStock ? (
          <>
            <Checkbox
              isSelected={shownOutOfStock.isSelected}
              onChange={shownOutOfStock.onChange}
              className="sk:mt-1 sk:items-start sk:rounded-option sk:px-3 sk:py-2 sk:hover:bg-fill-quaternary"
            >
              <span className="sk:flex sk:flex-col sk:text-left">
                <span className="sk:font-medium sk:text-fg">
                  Only out of stock
                </span>
                <span className="sk:text-xs sk:leading-4 sk:text-fg-secondary">
                  Orders no provider has inventory for
                </span>
              </span>
            </Checkbox>
            <Divider />
          </>
        ) : null}

        {isV2 ? (
          <>
            <Checkbox
              isSelected={values.hold === HOLD_EXCEPTION}
              onChange={isSelected =>
                onChange({ hold: isSelected ? HOLD_EXCEPTION : '' })
              }
              className="sk:mt-1 sk:items-start sk:rounded-option sk:px-3 sk:py-2 sk:hover:bg-fill-quaternary"
            >
              <span className="sk:flex sk:flex-col sk:text-left">
                <span className="sk:font-medium sk:text-fg">Only held</span>
                <span className="sk:text-xs sk:leading-4 sk:text-fg-secondary">
                  Orders with an exception to clear
                </span>
              </span>
            </Checkbox>
            <Divider />
          </>
        ) : null}

        {shows('marketplace') ? (
          <Row label="Marketplace">
            <MultiSelect
              aria-label="Marketplace"
              options={marketplaces}
              values={parseList(values.marketplace)}
              onChange={next => onChange({ marketplace: joinList(next) })}
              placeholder="All marketplaces"
              className={TRIGGER_WIDTH}
            />
          </Row>
        ) : null}

        {isAccount ? (
          <Row label="Division">
            <DivisionFilter
              divisions={divisions}
              values={divisionFilter}
              onChange={ids => {
                setDivisionFilter(ids);
                onChange({ [DIVISION_FILTER_PARAM]: joinList(ids) });
              }}
              className={TRIGGER_WIDTH}
            />
          </Row>
        ) : null}

        {shows('marketplace_region_id') ? (
          <Row label="Region">
            <Select
              aria-label="Region"
              options={regions}
              value={values.marketplace_region_id}
              onChange={next => onChange({ marketplace_region_id: next })}
              placeholder="All regions"
              allowsEmptySelection
              className={TRIGGER_WIDTH}
            />
          </Row>
        ) : null}

        {providers && shows('fulfillment_provider_id') ? (
          <Row label="Provider">
            <Select
              aria-label="Provider"
              options={providers}
              value={values.fulfillment_provider_id}
              onChange={next => onChange({ fulfillment_provider_id: next })}
              placeholder="All providers"
              allowsEmptySelection
              className={TRIGGER_WIDTH}
            />
          </Row>
        ) : null}

        <Divider />

        {advancedKeys.length > 0 ? (
          <Button
            variant="ghost"
            size="md"
            className="sk:h-9 sk:w-full sk:justify-between sk:gap-2 sk:px-3"
            onPress={() => setAdvancedOpen(open => !open)}
          >
            <span className="sk:inline-flex sk:items-center sk:gap-1.5">
              <span className="sk:font-semibold sk:text-fg">
                Advanced filters
              </span>
              {activeAdvanced > 0 ? (
                <Badge tone="neutral" className="sk:px-1.5 sk:py-0">
                  {activeAdvanced}
                </Badge>
              ) : null}
            </span>
            {isAdvancedOpen ? (
              <CaretUpIcon size={10} weight="bold" />
            ) : (
              <CaretDownIcon size={10} weight="bold" />
            )}
          </Button>
        ) : null}

        {isAdvancedOpen && advancedKeys.length > 0 ? (
          <>
            {shows('items_total_qty') ? (
              <Row label="Items quantity">
                <TextField
                  label="Items quantity"
                  type="number"
                  placeholder="Any quantity"
                  value={values.items_total_qty || ''}
                  onChange={next => onChange({ items_total_qty: next })}
                  className={TRIGGER_WIDTH}
                />
              </Row>
            ) : null}

            {shows('created_by') ? (
              <Row label="Created by">
                <MultiSelect
                  aria-label="Created by"
                  options={users}
                  values={parseList(values.created_by)}
                  onChange={next => onChange({ created_by: joinList(next) })}
                  placeholder="Anyone"
                  className={TRIGGER_WIDTH}
                />
              </Row>
            ) : null}

            {hasDateFilters
              ? DATE_ROWS.filter(row => shows(row.name)).map(row => (
                  <Row key={row.name} label={row.label}>
                    <OrdersDateFilter
                      name={row.name}
                      label={row.label}
                      value={values[row.name]}
                      operator={toDateOperator(values[row.opName])}
                      onChange={onChange}
                    />
                  </Row>
                ))
              : null}
          </>
        ) : null}
      </div>
    </Popover>
  );
}
